import { queryOptions, useQuery } from "@tanstack/react-query";
import type { Asset, AssetType, ReservationRequest } from "@/v2/shared/model";
import {
  fetchAssets,
  fetchAvailableAssets,
  fetchDossierById,
  fetchRequestById,
  fetchRequests,
  fetchRequestsByRequester,
  type DossierWithRequest,
} from "@/v2/data/db";
import { v2Keys } from "@/v2/data/keys";

export const assetsQuery = queryOptions({
  queryKey: v2Keys.assets.list(),
  queryFn: fetchAssets,
});

export function availableAssetsQuery(
  startDate: string,
  endDate: string,
  type?: AssetType,
) {
  return queryOptions({
    queryKey: v2Keys.assets.available(startDate, endDate, type),
    queryFn: () => fetchAvailableAssets(startDate, endDate, type),
    enabled: Boolean(startDate && endDate),
  });
}

export const requestsQuery = queryOptions({
  queryKey: v2Keys.requests.list(),
  queryFn: fetchRequests,
});

export function requestsByRequesterQuery(requesterId: string) {
  return queryOptions({
    queryKey: v2Keys.requests.byRequester(requesterId),
    queryFn: () => fetchRequestsByRequester(requesterId),
  });
}

export function requestDetailQuery(id: string) {
  return queryOptions({
    queryKey: v2Keys.requests.detail(id),
    queryFn: () => fetchRequestById(id),
  });
}

export function dossierDetailQuery(id: string) {
  return queryOptions({
    queryKey: v2Keys.requests.dossier(id),
    queryFn: () => fetchDossierById(id),
  });
}

export function useAssets(): ReturnType<typeof useQuery<ReadonlyArray<Asset>>> {
  return useQuery(assetsQuery);
}

export function useAvailableAssets(
  startDate: string,
  endDate: string,
  type?: AssetType,
): ReturnType<typeof useQuery<ReadonlyArray<Asset>>> {
  return useQuery(availableAssetsQuery(startDate, endDate, type));
}

export function useRequests(): ReturnType<
  typeof useQuery<ReadonlyArray<ReservationRequest>>
> {
  return useQuery(requestsQuery);
}

export function useRequestsByRequester(
  requesterId: string,
): ReturnType<typeof useQuery<ReadonlyArray<ReservationRequest>>> {
  return useQuery(requestsByRequesterQuery(requesterId));
}

export function useRequestDetail(
  id: string,
): ReturnType<typeof useQuery<ReservationRequest | null>> {
  return useQuery(requestDetailQuery(id));
}

export function useDossierDetail(
  id: string,
): ReturnType<typeof useQuery<DossierWithRequest | null>> {
  return useQuery(dossierDetailQuery(id));
}
